import DashboardChartFilters from "@components/DashboardChartFilters";
import DashboardChartCaseTypeFilter from "@components/DashboardChartFilters/DashboardChartCaseTypeFilter";
import { DashboardChartCustomerFilter } from "@components/DashboardChartFilters/DashboardChartCustomerFilter";
import DashboardChartWrapper from "@components/DashboardChartWrapper";
import DashboardDefaultChartEmptyState from "@components/EmptyStates/DashboardDefaultChartEmptyState";
import { QueryLoaderWrapper } from "@components/Loaders";
import {
  CustomChartStyleWrapper,
  StripedColorStackedBarChartWrapper,
  ToggletipText,
} from "src/style";
import React, { useEffect, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import Skeleton from "react-loading-skeleton";
import { sendEventChartUsed } from "src/services/dashboardAnalyticEventService";
import { dashboardChartFilterAndActionTypes } from "@utils/constants/dashboard/analyticDashboardEvents";
import {
  DASHBOARD_CASE_TYPES,
  DASHBOARD_CHART_TYPES,
} from "@utils/constants/dashboard/dashboardModuleConstants";

import { getColumnChartOptions } from "./helpers";
import useLawsuitByCustomerOverTimeColumnChart from "./useLawsuitByCustomerOverTimeColumnChartQuery";
import { CaseType } from "@customTypes/components/filters";
import { SimplyfiedContact } from "@customTypes/user/contact";
import ChartActions from "@components/ChartActions/ChartActions";

const LawsuitByCustomerOverTimeColumnChart = () => {
  const [caseTypeFilter, setCaseTypeFilter] = useState(DASHBOARD_CASE_TYPES.ALL);
  const [customerFilter, setCustomerFilter] = useState<SimplyfiedContact[]>([]);

  const header = {
    title: "Quantidade de processos e casos por cliente",
    description:
      "Busque pelo nome do cliente para ver a quantidade mensal de processos e casos cadastrados relacionados a ele e analisar a demanda conforme os objetivos do escritório.",
  };

  const toggleTip = {
    title: "Processos e casos por clientes",
    text: (
      <ToggletipText>
        Cada processo e caso é considerado apenas no mês do seu cadastro.
      </ToggletipText>
    ),
    onClickCallback: () => {
      sendEventChartUsed(
        DASHBOARD_CHART_TYPES.LAWSUIT_BY_CUSTOMER_OVER_TIME,
        dashboardChartFilterAndActionTypes.TOGGLE_TIP_OPENED
      );
    },
    callToAction: {
      text: "Acessar central de ajuda",
      callback: () => {
        sendEventChartUsed(
          DASHBOARD_CHART_TYPES.LAWSUIT_BY_CUSTOMER_OVER_TIME,
          dashboardChartFilterAndActionTypes.HELP_CENTER_CLICK
        );
        window.open(
          "https://astrea.aurum.com.br/pt-BR/articles/8043384-como-analisar-a-quantidade-de-processos-e-casos-por-cliente",
          "_blank"
        );
      },
    },
  };

  const [queryStatus, lawsuitByCustomerOverTimeResponse, fetchedTime, refetch] =
    useLawsuitByCustomerOverTimeColumnChart(
      customerFilter,
      caseTypeFilter.queryParam as CaseType[]
    );

  const setCaseTypeFilterAndSendMixPanelEvent = (filter: {
    queryParam: CaseType[];
    label: string;
  }) => {
    setCaseTypeFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.LAWSUIT_BY_CUSTOMER_OVER_TIME,
      dashboardChartFilterAndActionTypes.LAWSUIT_TYPE,
      filter
    );
  };

  const setCustomerFilterAndSendMixPanelEvent = (
    filter: React.SetStateAction<SimplyfiedContact[]>
  ) => {
    setCustomerFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.LAWSUIT_BY_CUSTOMER_OVER_TIME,
      dashboardChartFilterAndActionTypes.CUSTOMER_SEARCH
    );
  };

  const ChartSection = () => {
    const containerRef = useRef<HTMLDivElement>();

    useEffect(() => {
      if (containerRef.current) containerRef.current.scrollIntoView();
    }, [containerRef]);
    if (!customerFilter || customerFilter.length < 1) return null;

    const { xAxis, series, groups, noData } = lawsuitByCustomerOverTimeResponse;

    const options = getColumnChartOptions(xAxis, groups);

    const shouldRenderEmptyState = noData;
    const hasMoreThanOneCustomerSelected = customerFilter.length > 1;

    return (
      <>
        <DashboardChartFilters>
          <DashboardChartCaseTypeFilter
            currentFilter={caseTypeFilter}
            setFilter={setCaseTypeFilterAndSendMixPanelEvent}
          />
        </DashboardChartFilters>
        {shouldRenderEmptyState ? (
          <DashboardDefaultChartEmptyState
            title={`Nenhum processo ou caso desse${
              hasMoreThanOneCustomerSelected ? "s" : ""
            } cliente${hasMoreThanOneCustomerSelected ? "s" : ""} foi encontrado`}
            subtitle={`Verifique o filtro ou se o${
              hasMoreThanOneCustomerSelected ? "s" : ""
            } cliente${hasMoreThanOneCustomerSelected ? "s" : ""} selecionado${
              hasMoreThanOneCustomerSelected ? "s" : ""
            } possu${
              hasMoreThanOneCustomerSelected ? "em" : "i"
            } processos ou casos cadastrados no Astrea.`}
            chartId={DASHBOARD_CHART_TYPES.LAWSUIT_BY_CUSTOMER_OVER_TIME}
            link="https://astrea.aurum.com.br/pt-BR/articles/8043384-como-analisar-a-quantidade-de-processos-e-casos-por-cliente"
          />
        ) : (
          <>
            <ChartActions updatedAt={fetchedTime} refetchFunction={refetch} />
            <CustomChartStyleWrapper ref={containerRef}>
              <StripedColorStackedBarChartWrapper evenColor="primary900">
                <QueryLoaderWrapper
                  queryStatus={queryStatus}
                  skeletonComponent={<Skeleton height={350 + 15} />}
                >
                  <ReactApexChart
                    options={options}
                    series={series}
                    type="bar"
                    height={350}
                    width="95%"
                  />
                </QueryLoaderWrapper>
              </StripedColorStackedBarChartWrapper>
            </CustomChartStyleWrapper>
          </>
        )}
      </>
    );
  };

  return (
    <DashboardChartWrapper
      title={header.title}
      description={header.description}
      toggleTip={toggleTip}
    >
      <DashboardChartCustomerFilter
        setCustomerFunction={setCustomerFilterAndSendMixPanelEvent}
        selectedCustomers={customerFilter}
      />
      <ChartSection />
    </DashboardChartWrapper>
  );
};

export default LawsuitByCustomerOverTimeColumnChart;
